import { useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { useAsync } from '@/lib/useAsync'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState } from '@/components/ui/primitives'
import { Field, Input, Select } from '@/components/ui/form'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { ReferenceItem, ReferenceList } from '@/types/api'

/**
 * The managed picklists behind the customer form.
 *
 * <p>
 * These exist so the answers group. "Exhibition", "exhibition" and "Trade show" typed
 * into a free-text box are three answers to one question and no report can add them up,
 * which is the whole reason this screen is worth its own page.
 * </p>
 */

const LISTS: readonly { id: ReferenceList; label: string; hint: string }[] = [
  {
    id: 'BusinessType',
    label: 'Business types',
    hint: 'The legal form a customer trades as. Scoped to a country, because Sdn Bhd is Malaysian and Pte Ltd is not.',
  },
  {
    id: 'LeadSource',
    label: 'Lead sources',
    hint: 'Where a customer came from. Never scoped - a referral is a referral in every country.',
  },
  {
    id: 'DisqualifyReason',
    label: 'Disqualify reasons',
    hint: 'Why a real person was not a fit. Required to disqualify a lead, so the reasons can be counted. Never scoped.',
  },
]

/** The order the customer form shows them in, which is what these numbers are for. */
const DEFAULT_SORT = 'order'

export function ReferenceLists() {
  const [list, setList] = useState<ReferenceList>('BusinessType')
  const active = LISTS.find((entry) => entry.id === list)!

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Lists"
        hint="The dropdowns on the customer and lead forms. Retiring an entry hides it from new work and leaves the records already carrying it alone."
      />

      <Tabs
        active={list}
        onSelect={setList}
        tabs={LISTS.map((entry) => ({ id: entry.id, label: entry.label }))}
      />

      <ListEditor list={list} hint={active.hint} />
    </>
  )
}

function ListEditor({ list, hint }: { list: ReferenceList; hint: string }) {
  const { can } = useAuth()
  const { available } = useCountry()
  const toast = useToast()
  const [editing, setEditing] = useState<ReferenceItem | undefined>()
  const [adding, setAdding] = useState(false)

  const canManage = can('reference.manage')

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // includeInactive, because this is the screen where a retired entry has to be visible
  // in order to be brought back.
  const items = useAsync<ReferenceItem[]>(
    (signal) => api.get<ReferenceItem[]>(`/reference/${list}`, { includeInactive: true }, signal),
    [list],
  )

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  // Narrowed and ordered here rather than by the server.
  //
  // This endpoint answers the customer form's picklist as well as this screen, and it
  // answers it with the whole list because a dropdown needs the whole list. Paging it
  // would either break that caller or need a second endpoint over the same six rows.
  // The set is already in memory; searching and ordering it here is exact and costs a
  // render.
  const matching = useMemo(() => {
    const term = search.trim().toLowerCase()

    const filtered = (items.data ?? []).filter((item) => {
      if (term && !item.name.toLowerCase().includes(term)) return false
      if (status === 'active' && !item.isActive) return false
      if (status === 'retired' && item.isActive) return false
      return true
    })

    const [field, descending] = sort.startsWith('-') ? [sort.slice(1), true] : [sort, false]

    const ordered = [...filtered].sort((a, b) => {
      const by =
        field === 'name'
          ? a.name.localeCompare(b.name)
          : field === 'country'
            ? (a.countryIsoCode ?? '').localeCompare(b.countryIsoCode ?? '')
            : field === 'status'
              ? Number(b.isActive) - Number(a.isActive)
              : a.sortOrder - b.sortOrder

      // The declared order is the tie-break, because that is the order the customer form
      // shows them in - two entries that tie on the sorted field still read predictably.
      return (descending ? -by : by) || a.sortOrder - b.sortOrder
    })

    return ordered
  }, [items.data, search, status, sort])

  const totalPages = Math.max(1, Math.ceil(matching.length / pageSize))
  const current = Math.min(page, totalPages)
  const visible = matching.slice((current - 1) * pageSize, current * pageSize)

  async function run(action: () => Promise<unknown>, message: string) {
    try {
      await action()
      toast.show(message, 'success')
      items.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'That did not work', 'error')
    }
  }

  return (
    <Card>
      <CardHeader
        title={LISTS.find((entry) => entry.id === list)!.label}
        hint={hint}
        action={
          canManage && !adding ? (
            <Button size="sm" onClick={() => setAdding(true)}>
              Add entry
            </Button>
          ) : undefined
        }
      />

      {adding && (
        <NewEntry
          list={list}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false)
            items.reload()
          }}
        />
      )}

      <div className="px-5 pt-4">
        <ListToolbar
          sort={sort}
          onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}
          sortChoices={[
            { value: 'order', label: 'Declared order' },
            { value: 'name', label: 'Name A-Z' },
            { value: '-name', label: 'Name Z-A' },
            { value: 'country', label: 'Country' },
            { value: 'status', label: 'Status' },
          ]}
        >
          <div className="w-56">
            <Input
              placeholder="Search entry"
              value={search}
              onChange={(event) => refine(() => setSearch(event.target.value))}
            />
          </div>

          <FilterSelect
            label="Status"
            value={status}
            onChange={(next) => refine(() => setStatus(next))}
            width="w-40"
          >
            <option value="">Every entry</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </FilterSelect>
        </ListToolbar>
      </div>

      {items.error && <ErrorState title={items.error.title} detail={items.error.detail} onRetry={items.reload} />}

      <Busy busy={items.loading}>
        {items.data && matching.length === 0 && !adding && (
          <EmptyState
            title={items.data.length === 0 ? 'Nothing on this list yet' : 'Nothing matches that'}
            hint={
              items.data.length === 0
                ? 'Add the first entry and it appears on the customer form.'
                : 'Clear the search or the status filter to see the rest.'
            }
          />
        )}

        {items.data && matching.length > 0 && (
          <Table>
            <thead>
              <tr>
                <Th>Entry</Th>
                <Th>Country</Th>
                <Th numeric>Order</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {visible.map((item) =>
                editing?.id === item.id ? (
                  <tr key={item.id}>
                    <Td colSpan={5}>
                      <EditEntry
                        item={item}
                        onCancel={() => setEditing(undefined)}
                        onSaved={() => {
                          setEditing(undefined)
                          items.reload()
                        }}
                      />
                    </Td>
                  </tr>
                ) : (
                  <tr key={item.id}>
                    <Td>
                      <span className="font-medium">{item.name}</span>
                    </Td>
                    <Td className="text-muted">
                      {item.countryIsoCode ?? (
                        <span title="Offered in every country">Every country</span>
                      )}
                    </Td>
                    <Td numeric className="text-muted">
                      {item.sortOrder}
                    </Td>
                    <Td>
                      {item.isActive ? (
                        <Badge tone="accent">Active</Badge>
                      ) : (
                        <Badge tone="neutral">Retired</Badge>
                      )}
                    </Td>
                    <Td>
                      {canManage && (
                        <span className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => setEditing(item)}>
                            Edit
                          </Button>

                          {item.isActive ? (
                            <Button
                              size="sm"
                              onClick={() =>
                                void run(
                                  () => api.del(`/reference/${item.id}`),
                                  // The API removes an entry nothing points at and retires
                                  // one that is in use. Saying which happened beats a single
                                  // message that is half wrong.
                                  item.isInUse
                                    ? 'Retired. The customers already using it keep it.'
                                    : 'Removed',
                                )
                              }
                            >
                              {item.isInUse ? 'Retire' : 'Remove'}
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() =>
                                void run(
                                  () => api.patch(`/reference/${item.id}`, { isActive: true }),
                                  'Back on the list',
                                )
                              }
                            >
                              Restore
                            </Button>
                          )}
                        </span>
                      )}
                    </Td>
                  </tr>
                ),
              )}
            </tbody>
          </Table>
        )}

        {items.data && matching.length > 0 && (
          <Pagination
            page={current}
            pageSize={pageSize}
            totalCount={matching.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}
      </Busy>

      {/* Countries are shown by code in the table, so the reader needs to know the blank
          case is deliberate rather than missing data. */}
      {items.data && items.data.some((item) => item.countryId === null) && available.length > 0 && (
        <p className="border-t border-line-soft px-5 py-3 text-[12px] text-muted">
          An entry with no country is offered on every customer form.
        </p>
      )}
    </Card>
  )
}

function NewEntry({
  list,
  onClose,
  onCreated,
}: {
  list: ReferenceList
  onClose: () => void
  onCreated: () => void
}) {
  const { available } = useCountry()
  const toast = useToast()
  const [name, setName] = useState('')

  // Lead sources are never scoped, so the country control is not offered for them at all
  // rather than offered and ignored.
  const scoped = list === 'BusinessType'
  const [countryId, setCountryId] = useState('')
  const [saving, setSaving] = useState(false)

  async function create() {
    setSaving(true)
    try {
      await api.post('/reference', {
        list,
        countryId: scoped && countryId ? countryId : null,
        name: name.trim(),
      })

      toast.show('Added to the list', 'success')
      setName('')
      onCreated()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add it', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Every control on one line, and every hint under it. A hint hanging off one field makes
  // that field taller than the ones beside it, and `items-end` then lines up the bottom of
  // the hint with the bottom of the other boxes - which is what pushed this row out of true.
  return (
    <div className="border-b border-line-soft px-5 pb-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              className="w-64"
              value={name}
              placeholder={scoped ? 'Sdn Bhd' : 'Exhibition / trade show'}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        {scoped && (
          <Field label="Country">
            {(id) => (
              <Select
                id={id}
                className="w-56"
                value={countryId}
                onChange={(event) => setCountryId(event.target.value)}
              >
                <option value="">Every country</option>
                {available.map((country) => (
                  <option key={country.countryId} value={country.countryId}>
                    {country.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <div className="flex gap-2">
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name.trim()} onClick={() => void create()}>
            Add
          </Button>
        </div>
      </div>

      {scoped && (
        <p className="mt-2 text-[12px] text-muted">
          Leave as every country when the form applies everywhere.
        </p>
      )}
    </div>
  )
}

/**
 * Renames an entry and moves it in the order.
 *
 * The country is not editable. Moving an entry between countries would change what a
 * customer already carrying it means, and adding it to the other country is the honest
 * way to say the same thing.
 */
function EditEntry({
  item,
  onCancel,
  onSaved,
}: {
  item: ReferenceItem
  onCancel: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState(item.name)
  const [sortOrder, setSortOrder] = useState(String(item.sortOrder))
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/reference/${item.id}`, {
        name: name.trim(),
        sortOrder: Number(sortOrder),
      })

      toast.show('Entry saved', 'success')
      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-md border border-line bg-sunken p-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Name" required>
          {(id) => (
            <Input id={id} className="w-64" value={name} onChange={(event) => setName(event.target.value)} />
          )}
        </Field>

        <Field label="Order">
          {(id) => (
            <Input
              id={id}
              className="w-24"
              type="number"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
            />
          )}
        </Field>

        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name.trim()} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </div>

      <p className="mt-2 text-[12px] text-muted">
        Lower sorts first; 999 keeps an entry last. {item.countryIsoCode ? `${item.countryIsoCode} only` : 'Every country'}
        {item.isInUse && ' · in use by customers'}
      </p>
    </div>
  )
}
